import { useFrame, useThree } from '@react-three/fiber';
import { useRef } from 'react';
import * as THREE from 'three';
import { Vector2, Vector3 } from 'three';
import { CALLBACK_ORDER_BILLBOARD } from './FrameCallbackOrder';
import type { Number3 } from '../types';

type Props = {
  position?: Number3;
  scale?: Number3;
  respectPixelRatio?: boolean;
  children?: React.ReactNode;
};

const extraScale = 1;
const DEFAULT_SCALE: Number3 = [1, 1, 1];

export const AutoscaleGroup = ({
  position,
  scale = DEFAULT_SCALE,
  respectPixelRatio,
  children,
}: Props) => {
  const self = useRef<{
    position: Vector3;
    cameraPosition: Vector3;
    screenSize: Vector2;
  }>({
    position: new Vector3(),
    cameraPosition: new Vector3(0, 0, 0),
    screenSize: new Vector2(),
  });

  const camera = useThree((state) => state.camera);
  const gl = useThree((state) => state.gl);

  const groupReference = useRef<THREE.Group>(null);

  useFrame(() => {
    let scaleFactor = extraScale;

    if (respectPixelRatio) {
      scaleFactor /= gl.getPixelRatio();
    }

    if (groupReference.current) {
      if (camera instanceof THREE.OrthographicCamera) {
        scaleFactor *= 128 / extraScale / camera.zoom;
      } else if (camera instanceof THREE.PerspectiveCamera) {
        camera.getWorldPosition(self.current.cameraPosition);
        groupReference.current.getWorldPosition(self.current.position);

        const distance = self.current.cameraPosition.distanceTo(self.current.position);

        gl.getSize(self.current.screenSize);
        const screenHalfHeight = 0.5 * self.current.screenSize.height;
        const halfFov = (camera.fov * Math.PI) / 360;

        // slope == height / distance
        const slope = Math.tan(halfFov);

        const screenDistribution = screenHalfHeight / slope;

        scaleFactor *= distance / screenDistribution;
      }
      groupReference.current?.scale.set(
        scale[0] * scaleFactor,
        scale[1] * scaleFactor,
        scale[2] * scaleFactor
      );
    }
  }, CALLBACK_ORDER_BILLBOARD);

  return (
    <group ref={groupReference} position={position} scale={scale}>
      {children}
    </group>
  );
};
